import React, { useCallback, useEffect, useRef, useState } from "react";

export default function ServerVoicePanel({ socket, connected, serverName }) {
  const [joined, setJoined] = useState(false);
  const [muted, setMuted] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [status, setStatus] = useState("");

  const localStreamRef = useRef(null);
  const peerConnectionsRef = useRef(new Map());
  const remoteAudioRef = useRef(new Map());
  const joinedRef = useRef(false);
  const mutedRef = useRef(false);
  const speakingRef = useRef(false);
  const audioContextRef = useRef(null);
  const speakingFrameRef = useRef(0);
  const lastSpeakingEmitRef = useRef(0);

  useEffect(() => {
    joinedRef.current = joined;
  }, [joined]);

  useEffect(() => {
    mutedRef.current = muted;
  }, [muted]);

  const emitSpeaking = useCallback(
    (speaking, force = false) => {
      if (!socket?.connected) return;
      const now = Date.now();
      if (!force && now - lastSpeakingEmitRef.current < 180) return;
      if (!force && speakingRef.current === speaking) return;
      speakingRef.current = speaking;
      lastSpeakingEmitRef.current = now;
      socket.emit("server_voice_speaking", { speaking });
    },
    [socket]
  );

  const stopSpeakingMonitor = useCallback(() => {
    if (speakingFrameRef.current) {
      cancelAnimationFrame(speakingFrameRef.current);
      speakingFrameRef.current = 0;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    emitSpeaking(false, true);
  }, [emitSpeaking]);

  const startSpeakingMonitor = useCallback(
    (stream) => {
      stopSpeakingMonitor();
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.72;
      const source = ctx.createMediaStreamSource(stream);
      source.connect(analyser);
      const samples = new Uint8Array(analyser.fftSize);
      audioContextRef.current = ctx;

      const tick = () => {
        analyser.getByteTimeDomainData(samples);
        let sum = 0;
        for (let i = 0; i < samples.length; i += 1) {
          const centered = (samples[i] - 128) / 128;
          sum += centered * centered;
        }
        const rms = Math.sqrt(sum / samples.length);
        emitSpeaking(!mutedRef.current && rms > 0.045);
        speakingFrameRef.current = requestAnimationFrame(tick);
      };
      tick();
    },
    [emitSpeaking, stopSpeakingMonitor]
  );

  const closePeer = useCallback((peerId) => {
    const pc = peerConnectionsRef.current.get(peerId);
    if (pc) {
      pc.ontrack = null;
      pc.onicecandidate = null;
      pc.close();
      peerConnectionsRef.current.delete(peerId);
    }
    const audio = remoteAudioRef.current.get(peerId);
    if (audio) {
      audio.srcObject = null;
      audio.remove();
      remoteAudioRef.current.delete(peerId);
    }
  }, []);

  const stopClient = useCallback(() => {
    for (const peerId of [...peerConnectionsRef.current.keys()]) closePeer(peerId);
    stopSpeakingMonitor();
    if (localStreamRef.current) {
      for (const track of localStreamRef.current.getTracks()) track.stop();
      localStreamRef.current = null;
    }
    joinedRef.current = false;
    speakingRef.current = false;
    setParticipants([]);
    setJoined(false);
  }, [closePeer, stopSpeakingMonitor]);

  const leaveVoice = useCallback(() => {
    if (socket?.connected) socket.emit("server_voice_leave");
    stopClient();
    setStatus("");
  }, [socket, stopClient]);

  const ensurePeer = useCallback(
    (peerId) => {
      if (!socket || !localStreamRef.current) return null;
      const existing = peerConnectionsRef.current.get(peerId);
      if (existing) return existing;
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
      });
      for (const track of localStreamRef.current.getTracks()) {
        pc.addTrack(track, localStreamRef.current);
      }
      pc.onicecandidate = (event) => {
        if (event.candidate) socket.emit("server_voice_signal", { to: peerId, candidate: event.candidate });
      };
      pc.ontrack = (event) => {
        let audio = remoteAudioRef.current.get(peerId);
        if (!audio) {
          audio = document.createElement("audio");
          audio.autoplay = true;
          audio.playsInline = true;
          document.body.appendChild(audio);
          remoteAudioRef.current.set(peerId, audio);
        }
        audio.srcObject = event.streams[0];
        audio.play().catch(() => {});
      };
      peerConnectionsRef.current.set(peerId, pc);
      return pc;
    },
    [socket]
  );

  useEffect(() => {
    if (!socket) return undefined;

    const onParticipants = async (payload) => {
      const list = Array.isArray(payload?.participants) ? payload.participants : [];
      setParticipants(list);
      if (!joinedRef.current || !socket.id) return;
      const liveIds = new Set(list.map((p) => p.socketId).filter(Boolean));
      for (const peerId of [...peerConnectionsRef.current.keys()]) {
        if (!liveIds.has(peerId)) closePeer(peerId);
      }
      for (const p of list) {
        const peerId = p.socketId;
        if (!peerId || peerId === socket.id) continue;
        const pc = ensurePeer(peerId);
        if (!pc || pc.localDescription || socket.id <= peerId) continue;
        try {
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          socket.emit("server_voice_signal", { to: peerId, description: pc.localDescription });
        } catch (e) {
          setStatus(e.message || "Voice offer failed.");
        }
      }
    };

    const onSignal = async (payload) => {
      if (!joinedRef.current) return;
      const from = String(payload?.from || "");
      if (!from) return;
      const pc = ensurePeer(from);
      if (!pc) return;
      try {
        if (payload.description) {
          const desc = payload.description;
          await pc.setRemoteDescription(desc);
          if (desc.type === "offer") {
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);
            socket.emit("server_voice_signal", { to: from, description: pc.localDescription });
          }
        }
        if (payload.candidate) await pc.addIceCandidate(payload.candidate);
      } catch (e) {
        setStatus(e.message || "Voice signal failed.");
      }
    };

    const onPeerLeft = (payload) => {
      const peerId = String(payload?.socketId || "");
      if (peerId) closePeer(peerId);
    };

    const onJoined = () => {
      joinedRef.current = true;
      setJoined(true);
      setStatus("Connected.");
    };

    const onError = (payload) => {
      stopClient();
      setStatus(payload?.message || "Could not join voice.");
    };

    const onLeft = () => {
      stopClient();
      setStatus("");
    };

    socket.on("server_voice_joined", onJoined);
    socket.on("server_voice_left", onLeft);
    socket.on("server_voice_error", onError);
    socket.on("server_voice_participants", onParticipants);
    socket.on("server_voice_signal", onSignal);
    socket.on("server_voice_peer_left", onPeerLeft);
    return () => {
      socket.off("server_voice_joined", onJoined);
      socket.off("server_voice_left", onLeft);
      socket.off("server_voice_error", onError);
      socket.off("server_voice_participants", onParticipants);
      socket.off("server_voice_signal", onSignal);
      socket.off("server_voice_peer_left", onPeerLeft);
    };
  }, [closePeer, ensurePeer, socket, stopClient]);

  useEffect(() => () => leaveVoice(), [leaveVoice]);

  useEffect(() => {
    if (!connected && joinedRef.current) {
      stopClient();
      setStatus("Disconnected.");
    }
  }, [connected, stopClient]);

  const joinVoice = async () => {
    if (!socket?.connected) {
      setStatus("Socket is not connected.");
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus("Voice needs microphone support in this browser.");
      return;
    }
    try {
      setStatus("Asking for mic...");
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      localStreamRef.current = stream;
      for (const track of stream.getAudioTracks()) track.enabled = !mutedRef.current;
      startSpeakingMonitor(stream);
      setStatus("Joining...");
      socket.emit("server_voice_join", { roomId: "server", muted: mutedRef.current });
    } catch (e) {
      setStatus(e.message || "Could not open microphone.");
    }
  };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    mutedRef.current = next;
    if (localStreamRef.current) {
      for (const track of localStreamRef.current.getAudioTracks()) track.enabled = !next;
    }
    if (next) emitSpeaking(false, true);
    if (socket?.connected && joined) socket.emit("server_voice_state", { muted: next });
  };

  return (
    <section className="server-voice-panel" aria-label="Server voice chat">
      <div className="server-voice-head">
        <div>
          <div className="server-voice-title">Voice</div>
          <div className="server-voice-subtitle">{serverName || "Current server"}</div>
        </div>
        <span className={joined ? "server-voice-live" : "server-voice-idle"}>{joined ? "Live" : "Idle"}</span>
      </div>
      <div className="server-voice-actions">
        {!joined ? (
          <button type="button" disabled={!connected} onClick={joinVoice}>
            Join
          </button>
        ) : (
          <>
            <button type="button" onClick={toggleMute}>{muted ? "Unmute" : "Mute"}</button>
            <button type="button" onClick={leaveVoice}>Leave</button>
          </>
        )}
      </div>
      {status ? <div className="server-voice-status">{status}</div> : null}
      <div className="server-voice-list">
        {participants.map((p) => (
          <div key={p.socketId} className={`server-voice-person${p.speaking ? " is-speaking" : ""}`}>
            <span className={p.muted ? "server-voice-dot is-muted" : p.speaking ? "server-voice-dot is-speaking" : "server-voice-dot"} />
            <strong>{p.userName || "Someone"}</strong>
            <span>{p.socketId === socket?.id ? "you" : p.muted ? "muted" : p.speaking ? "talking" : "listening"}</span>
          </div>
        ))}
        {!participants.length ? <div className="server-voice-empty">No one in voice.</div> : null}
      </div>
    </section>
  );
}
